import { ReactNode } from "react";
import styles from "../aic.module.css";
import { classMerge } from "../utility/classMerge";
import { AitAddColumnButton } from "./AitAddColumnButton";
import { AitBlankRow } from "./AitBlankRow";
import { AitColumnTrafficLight } from "./AitColumnTrafficLight";
import { AitTopBorderCell } from "./AitTopBorderCell";
import { useTableContext } from "./TableContextProvider";

interface AitBorderRowProps {
  id: string;
  noBorder?: boolean;
  spaceBefore?: boolean;
  spaceAfter?: boolean;
  topBorder?: boolean;
}

/**
 * Render border and spacing rows for the table, optionally with column
 * controls when editable.
 *
 * @param props - Component props
 * @param props.id - The base id for rows
 * @param props.noBorder - Skip rendering the main border row
 * @param props.spaceBefore - Render a space-before row
 * @param props.spaceAfter - Render a space-after row
 * @param props.topBorder - Render the top border with column buttons when editable
 */
export const AitBorderRow = (props: AitBorderRowProps): ReactNode => {
  const { state } = useTableContext();
  const topRow =
    state?.processedTableData?.headerData && state.processedTableData.headerData.rows.length > 0
      ? state.processedTableData.headerData.rows[0].cells
      : state?.processedTableData?.bodyData && state.processedTableData.bodyData.length > 0
        ? state.processedTableData.bodyData[0].rows[0].cells
        : [];
  const originalTopRow =
    state?.originalTableData.headerData && state.originalTableData.headerData.rows.length > 0
      ? state.originalTableData.headerData.rows[0].cells
      : state?.originalTableData.bodyData && state.originalTableData.bodyData.length > 0
        ? state.originalTableData.bodyData[0].rows[0].cells
        : [];
  const cis = topRow.map((c) => ({
    aitid: c.aitid,
    originalIx: originalTopRow.findIndex((o) => c.aitid === o.aitid),
    columnId: c.columnId,
  }));

  return !state || !state.processedTableData?.columnRepeats ? null : (
    <>
      {props.topBorder && (
        <tr id={`${props.id}`}>
          <td className={styles.aitCell}>
            {state.editable && (
              <div
                className={classMerge([styles.aitAieHolder, styles.aitColumnButtons])}
                style={{ position: "relative" }}
              >
                <AitAddColumnButton originalColumnIndex={-1} />
              </div>
            )}
          </td>
          {cis.map(
            (c, ci): React.ReactElement => (
              <AitTopBorderCell
                cellid={c.aitid}
                columnIndex={ci}
                originalColumnIndex={c.originalIx}
                columnId={c.columnId}
                key={ci}
              />
            ),
          )}
          <td
            className={styles.aitCell}
            style={{ position: "relative" }}
          >
            <AitColumnTrafficLight cellid="final" />
          </td>
        </tr>
      )}
      {props.spaceBefore && <AitBlankRow id={`${props.id}-spacebeforerow`} />}
      {!props.noBorder && (
        <AitBlankRow
          id={`${props.id}-borderrow`}
          border
        />
      )}
      {props.spaceAfter && <AitBlankRow id={`${props.id}-spaceafterrow`} />}
    </>
  );
};

AitBorderRow.displayName = "AitBorderRow";
